import { useState, useCallback, useRef, useEffect } from 'react';
import { Paperclip, Upload, Trash2, FileText, Image, Film, Music, Archive, File, X, Loader2, Eye, Maximize2 } from 'lucide-react';
import clsx from 'clsx';
import { uploadFile, getFileUrl, deleteFile } from '@/db/storage';
import { useDialogStore } from '@/store/dialogStore';

const IMAGE_EXTS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'];
const PDF_EXTS = ['pdf'];

interface AttachmentsSectionProps {
  taskId: string;
  attachments: string[];
  onUpdate: (attachments: string[]) => void;
  disabled?: boolean;
}

const MAX_FILE_SIZE = 10 * 1024 * 1024;

function _formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getFileExtension(path: string): string {
  const name = path.split('/').pop() || '';
  const ext = name.split('.').pop()?.toLowerCase() || '';
  return ext;
}

function getOriginalName(path: string): string {
  // Path format: userId/taskId/timestamp-random.ext
  const name = path.split('/').pop() || path;
  // Remove the timestamp-random prefix: "1234567890-abc123.pdf" -> show just filename
  return name;
}

function getFileIcon(ext: string) {
  const imageExts = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'];
  const videoExts = ['mp4'];
  const audioExts = ['mp3', 'wav', 'ogg'];
  const archiveExts = ['zip'];
  const docExts = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt', 'csv'];

  if (imageExts.includes(ext)) return Image;
  if (videoExts.includes(ext)) return Film;
  if (audioExts.includes(ext)) return Music;
  if (archiveExts.includes(ext)) return Archive;
  if (docExts.includes(ext)) return FileText;
  return File;
}

function getFileTypeLabel(ext: string): string {
  const labels: Record<string, string> = {
    jpg: 'JPEG', jpeg: 'JPEG', png: 'PNG', gif: 'GIF', webp: 'WebP', svg: 'SVG',
    pdf: 'PDF', doc: 'Word', docx: 'Word', xls: 'Excel', xlsx: 'Excel',
    ppt: 'PowerPoint', pptx: 'PowerPoint', txt: 'Testo', csv: 'CSV',
    zip: 'Archivio', mp4: 'Video', mp3: 'Audio', wav: 'Audio', ogg: 'Audio',
  };
  return labels[ext] || ext.toUpperCase();
}

export function AttachmentsSection({ taskId, attachments, onUpdate, disabled }: AttachmentsSectionProps) {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [signedUrls, setSignedUrls] = useState<Record<string, string>>({});
  const [lightbox, setLightbox] = useState<string | null>(null);
  const [expandedPdf, setExpandedPdf] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const confirm = useDialogStore((s) => s.confirm);

  // Pre-fetch signed URLs for previewable files
  useEffect(() => {
    const previewable = attachments.filter(p => {
      const ext = getFileExtension(p);
      return IMAGE_EXTS.includes(ext) || PDF_EXTS.includes(ext);
    });
    let cancelled = false;
    previewable.forEach(async (path) => {
      if (signedUrls[path]) return;
      try {
        const url = await getFileUrl(path);
        if (!cancelled) setSignedUrls(prev => ({ ...prev, [path]: url }));
      } catch { /* ignore */ }
    });
    return () => { cancelled = true; };
  }, [attachments]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFiles = useCallback(async (files: FileList | File[]) => {
    const fileArray = Array.from(files);
    if (fileArray.length === 0) return;

    setError(null);
    setUploading(true);

    const newPaths: string[] = [];
    try {
      for (let i = 0; i < fileArray.length; i++) {
        const file = fileArray[i];
        if (file.size > MAX_FILE_SIZE) {
          setError(`"${file.name}" supera il limite di 10 MB`);
          continue;
        }
        setUploadProgress(`${i + 1}/${fileArray.length}: ${file.name}`);
        const result = await uploadFile(taskId, file);
        newPaths.push(result.path);
      }
      if (newPaths.length > 0) {
        onUpdate([...attachments, ...newPaths]);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore durante il caricamento');
    } finally {
      setUploading(false);
      setUploadProgress(null);
    }
  }, [taskId, attachments, onUpdate]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    if (!disabled && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  }, [disabled, handleFiles]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) setDragOver(true);
  }, [disabled]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  }, []);

  const handleDelete = useCallback(async (path: string) => {
    const fileName = getOriginalName(path);
    const confirmed = await confirm({
      title: 'Elimina allegato',
      message: `Vuoi eliminare "${fileName}"? Questa azione non può essere annullata.`,
      confirmLabel: 'Elimina',
      variant: 'danger',
    });
    if (!confirmed) return;

    try {
      await deleteFile(path);
      onUpdate(attachments.filter(a => a !== path));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore durante l\'eliminazione');
    }
  }, [attachments, onUpdate, confirm]);

  const handleOpen = useCallback(async (path: string) => {
    try {
      const url = await getFileUrl(path);
      window.open(url, '_blank');
    } catch {
      setError('Impossibile aprire il file');
    }
  }, []);

  return (
    <div
      className="px-4 py-4 border-t border-border-primary"
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
    >
      <div className="flex items-center justify-between mb-3">
        <h3 className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-text-quaternary font-medium">
          <Paperclip className="w-3 h-3" />
          Allegati
          {attachments.length > 0 && (
            <span className="text-text-quaternary">({attachments.length})</span>
          )}
        </h3>
        {!disabled && (
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="flex items-center gap-1 px-2 py-0.5 rounded text-[10px] text-text-tertiary hover:text-accent-violet hover:bg-surface-hover transition-colors disabled:opacity-50"
          >
            <Upload className="w-3 h-3" />
            Carica
          </button>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="hidden"
        onChange={(e) => {
          if (e.target.files) handleFiles(e.target.files);
          e.target.value = '';
        }}
      />

      {/* Error message */}
      {error && (
        <div className="flex items-center gap-2 mb-2 p-2 rounded-lg bg-red-500/10 border border-red-500/20 text-[11px] text-red-400">
          <span className="flex-1">{error}</span>
          <button onClick={() => setError(null)} className="shrink-0 hover:text-red-300">
            <X className="w-3 h-3" />
          </button>
        </div>
      )}

      {/* Upload progress */}
      {uploading && (
        <div className="flex items-center gap-2 mb-2 p-2 rounded-lg bg-accent-violet/10 border border-accent-violet/20 text-[11px] text-accent-violet">
          <Loader2 className="w-3 h-3 animate-spin shrink-0" />
          <span className="truncate">{uploadProgress || 'Caricamento...'}</span>
        </div>
      )}

      {/* Drop zone when empty or dragging */}
      {(attachments.length === 0 || dragOver) && !disabled && (
        <div
          className={clsx(
            'flex flex-col items-center justify-center gap-1.5 py-4 rounded-lg border border-dashed transition-colors',
            dragOver
              ? 'border-accent-violet bg-accent-violet/10 text-accent-violet'
              : 'border-border-secondary text-text-quaternary hover:border-border-tertiary hover:text-text-tertiary cursor-pointer',
            attachments.length > 0 && 'mb-2'
          )}
          onClick={() => !dragOver && fileInputRef.current?.click()}
        >
          <Upload className="w-5 h-5" />
          <span className="text-[11px]">
            {dragOver ? 'Rilascia per caricare' : 'Trascina file qui o clicca per caricare'}
          </span>
          <span className="text-[9px] text-text-quaternary">Max 10 MB per file</span>
        </div>
      )}

      {/* Attachment list */}
      {attachments.length > 0 && (
        <div className="space-y-1.5">
          {attachments.map((path, i) => {
            const ext = getFileExtension(path);
            const Icon = getFileIcon(ext);
            const fileName = getOriginalName(path);
            const typeLabel = getFileTypeLabel(ext);
            const isImage = IMAGE_EXTS.includes(ext);
            const isPdf = PDF_EXTS.includes(ext);
            const previewUrl = signedUrls[path];

            return (
              <div key={i} className="rounded-lg bg-surface-tertiary/50 border border-border-primary hover:border-border-secondary transition-colors group overflow-hidden">
                {/* Image thumbnail */}
                {isImage && previewUrl && (
                  <div
                    className="relative cursor-pointer"
                    onClick={() => setLightbox(previewUrl)}
                  >
                    <img
                      src={previewUrl}
                      alt={fileName}
                      className="w-full max-h-48 object-cover"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-black/0 hover:bg-black/20 transition-colors flex items-center justify-center">
                      <Maximize2 className="w-5 h-5 text-white opacity-0 group-hover:opacity-80 transition-opacity drop-shadow-lg" />
                    </div>
                  </div>
                )}

                {/* PDF inline preview */}
                {isPdf && expandedPdf === path && previewUrl && (
                  <iframe
                    src={previewUrl}
                    className="w-full h-72 border-b border-border-primary"
                    title={fileName}
                  />
                )}

                {/* File info row */}
                <div
                  className="flex items-center gap-2 p-2 cursor-pointer"
                  onClick={() => handleOpen(path)}
                >
                  <div className="w-7 h-7 rounded-md bg-surface-hover flex items-center justify-center shrink-0">
                    <Icon className="w-3.5 h-3.5 text-text-tertiary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[11px] text-text-primary truncate">{fileName}</div>
                    <div className="text-[9px] text-text-quaternary">{typeLabel}</div>
                  </div>
                  {isPdf && previewUrl && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setExpandedPdf(expandedPdf === path ? null : path);
                      }}
                      className="shrink-0 p-1 rounded hover:bg-surface-hover text-text-quaternary hover:text-accent-violet transition-all"
                      title={expandedPdf === path ? 'Chiudi anteprima' : 'Anteprima PDF'}
                    >
                      <Eye className="w-3 h-3" />
                    </button>
                  )}
                  {!disabled && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(path);
                      }}
                      className="shrink-0 p-1 rounded hover:bg-red-500/10 text-text-quaternary opacity-0 group-hover:opacity-100 hover:text-red-400 transition-all"
                      title="Elimina allegato"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Lightbox overlay for images */}
      {lightbox && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-8 cursor-pointer"
          onClick={() => setLightbox(null)}
        >
          <button
            className="absolute top-4 right-4 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
            onClick={() => setLightbox(null)}
          >
            <X className="w-5 h-5" />
          </button>
          <img
            src={lightbox}
            alt="Preview"
            className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}
